import dayjs from 'dayjs';
import React, { useCallback, useContext, useState } from 'react';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';
import EditIcon from '@material-ui/icons/Edit';
import {
  PromptContext,
  PromptProvider,
  SnackbarContext,
  SnackbarVariant,
  abortableFetch,
  usePrompts,
} from '@eas/common-web';
import { useUnitAbsenceDialogEdit } from '@modules/planner/unit-menu/unit-absence-dialog';
import { Absence } from '@models';
import { EvidenceApiUrl } from '@enums';

export const AbsenceElement = ({
  left = 0,
  width = 0,
  absence,
  refreshData,
}: {
  left: number;
  width: number;
  absence: Absence;
  refreshData?: () => void;
}) => {
  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const { testPrompt } = useContext(PromptContext);
  const { showSnackbar } = useContext(SnackbarContext);

  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    console.log({ rect });
    setMenuPosition({
      top: rect.bottom + window.scrollY,
      left: event.clientX,
    });
  }, []);

  usePrompts(
    [
      {
        key: 'ABSENCE_DELETE_CONFIRM_DIALOG',
        dialogTitle: 'Zmazať absenciu',
        dialogText: 'Naozaj si želáte zmazať absenciu?',
      },
    ],
    []
  );

  const { key: absenceEditDialogKey } = useUnitAbsenceDialogEdit({ absence });

  return (
    <PromptProvider>
      <div
        ref={anchorRef}
        onContextMenu={handleContextMenu}
        style={{
          position: 'absolute',
          height: '100%',
          zIndex: 1,
          width,
          left,
          background: `repeating-linear-gradient(135deg, #FFF 0 3px, ${theme.palette.error.light}64 0px 5px )`,
          backgroundSize: 410,
          boxShadow: menuPosition ? 'inset 0 0px 2px 4px #fb0000' : 'none',
        }}
      />
      <Menu
        anchorEl={anchorRef.current}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition ?? undefined}
        open={menuPosition !== null}
        onClose={() => {
          setMenuPosition(null);
        }}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'top',
          horizontal: left === 0 ? 'right' : left,
        }}
      >
        <MenuList>
          <Typography
            variant="body1"
            style={{ padding: '0 16px', fontWeight: 'bold' }}
          >
            Absencia <br />
          </Typography>
          <Typography variant="body1" style={{ padding: '0 16px 8px 16px' }}>
            Trvanie:{' '}
            {absence.fromDate && dayjs(absence.fromDate).format('DD.MM.YYYY')}
            {absence.toDate &&
              ` - ${dayjs(absence.toDate).format('DD.MM.YYYY')}`}
            <br />
            Typ: {absence.absenceType?.name}
            <br />
            Poznámka: {absence.note} <br />
            <hr />
          </Typography>

          <MenuItem
            onClick={async () => {
              testPrompt({
                key: absenceEditDialogKey,
                submitCallback: async () => {},
              });
            }}
          >
            <EditIcon />
            <Typography variant="body1" style={{ marginLeft: 8 }}>
              Upraviť absenciu
            </Typography>
          </MenuItem>

          <MenuItem
            onClick={async () => {
              testPrompt({
                key: 'ABSENCE_DELETE_CONFIRM_DIALOG',
                submitCallback: async () => {
                  try {
                    await abortableFetch(
                      `${EvidenceApiUrl.ABSENCES}/${absence.id}`,
                      {
                        method: 'DELETE',
                      }
                    ).none();
                    refreshData?.();
                  } catch (error) {
                    const msg =
                      (error as { code: string })?.code ?? 'neznáma chyba';
                    showSnackbar(
                      `Absenciu sa nepodarilo zmazať: ${msg}`,
                      SnackbarVariant.ERROR
                    );
                  }
                },
              });
            }}
          >
            <ClearIcon color="error" />
            <Typography variant="body1" style={{ marginLeft: 8 }}>
              Zmazať absenciu
            </Typography>
          </MenuItem>
        </MenuList>
      </Menu>
    </PromptProvider>
  );
};
